import { Howl } from 'howler';

import { AUDIO_TYPES, soundMap } from './audio.types';

class AudioManager {
  private sounds: Map<AUDIO_TYPES, Howl> = new Map();
  private music: Howl | null = null;
  private isMuted = false;
  private isLoaded = false;
  private currentMusicType: AUDIO_TYPES | null = null;

  init(isMuted: boolean) {
    this.isMuted = isMuted;
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.music) {
      this.isMuted ? this.music.pause() : this.music.play();
    }
  }

  loadSounds(types: AUDIO_TYPES[] = Object.keys(soundMap) as AUDIO_TYPES[]) {
    if (this.isLoaded) return;
    this.isLoaded = true;

    for (const type of types) {
      const src = soundMap[type];
      if (src && !this.sounds.has(type)) {
        const sound = new Howl({
          src: [src],
          preload: true,
          volume: 1,
        });
        this.sounds.set(type, sound);
      }
    }
  }

  playSound(type: AUDIO_TYPES) {
    if (this.isMuted) return;

    let sound = this.sounds.get(type);
    if (!sound) {
      const src = soundMap[type];
      if (!src) return;

      sound = new Howl({
        src: [src],
        preload: true,
        volume: 1,
      });
      this.sounds.set(type, sound);
    }

    try {
      sound.play();
    } catch (e) {
      console.warn(`Failed to play sound ${type}`, e);
    }
  }

  playMusic(type: AUDIO_TYPES, loop = true) {
    if (this.currentMusicType === type && this.music?.playing()) return;

    if (this.music) {
      this.music.stop();
      this.music = null;
    }

    const src = soundMap[type];
    if (!src) return;

    const newMusic = new Howl({
      src: [src],
      loop,
      volume: 1,
    });

    this.sounds.set(type, newMusic);
    this.music = newMusic;
    this.currentMusicType = type;

    if (!this.isMuted) {
      try {
        this.music.play();
      } catch (e) {
        console.warn(`Failed to play music ${type}`, e);
      }
    }
  }

  getMuted() {
    return this.isMuted;
  }

  setVolume(volume: number) {
    this.sounds.forEach((s) => s.volume(volume));
    if (this.music) this.music.volume(volume);
  }
}

export const audioManager = new AudioManager();
